/**
 * Ploufe — "Ploufe bij mensen thuis" sfeerbeeld-marquee.
 *
 * De track (de <ul> hierbinnen) is en blijft ÉÉN flex-rij. Deze class
 * dupliceert de bestaande kinderen van diezelfde track precies één keer (nooit
 * een tweede <ul>!), zodat een CSS-animatie 'm naadloos van translateX(0) naar
 * translateX(-50%) kan schuiven. Slepen met muis of vinger neemt die animatie
 * tijdelijk over via pointer events en geeft 'm daarna exact op dezelfde
 * voortgang terug, via een negatieve animation-delay — geen sprong.
 *
 * Bij prefers-reduced-motion doet dit script niets: geen duplicaten, geen
 * animatie, gewoon de native, met de vinger/muis scrollbare <ul> die de
 * Liquid-template al rendert.
 */
class PloufeLifestyleCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-plf-carousel-track]');
    if (!this.track) return;

    this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.cloned = false;
    this.singleSetWidth = 0;
    this.dragging = false;
    this.startX = 0;
    this.startOffset = 0;
    this.currentOffset = 0;

    this._onPointerDown = this._onPointerDown.bind(this);
    this._onPointerMove = this._onPointerMove.bind(this);
    this._onPointerUp = this._onPointerUp.bind(this);
    this._onReducedMotionChange = this._onReducedMotionChange.bind(this);

    this._setup();
    this.reducedMotionQuery.addEventListener('change', this._onReducedMotionChange);
  }

  disconnectedCallback() {
    this.track.removeEventListener('pointerdown', this._onPointerDown);
    this._endDrag();
    this.reducedMotionQuery.removeEventListener('change', this._onReducedMotionChange);
  }

  _onReducedMotionChange() {
    this._endDrag();
    this.track.removeEventListener('pointerdown', this._onPointerDown);
    this._removeClones();
    this.track.style.transform = '';
    this.track.style.animationDelay = '';
    this._setup();
  }

  _setup() {
    if (this.reducedMotionQuery.matches) return;
    this._cloneOnce();
    this.track.addEventListener('pointerdown', this._onPointerDown);
  }

  /**
   * Dupliceert de huidige (echte) kinderen van de track precies één keer, in
   * dezelfde track — niet als tweede <ul> ernaast. Zo blijft de track altijd
   * één enkele flex-rij, nu met 2x dezelfde set, en klopt translateX(-50%)
   * ongeacht hoeveel blocks de winkelier heeft toegevoegd (4 tot 20).
   */
  _cloneOnce() {
    if (this.cloned) return;
    const originalItems = Array.from(this.track.children);
    if (!originalItems.length) return;

    this.singleSetWidth = this.track.scrollWidth;

    originalItems.forEach((item) => {
      const clone = item.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      clone.querySelectorAll('a').forEach((link) => link.setAttribute('tabindex', '-1'));
      this.track.appendChild(clone);
    });

    this.cloned = true;
  }

  _removeClones() {
    if (!this.cloned) return;
    const items = Array.from(this.track.children);
    const half = Math.floor(items.length / 2);
    items.slice(half).forEach((el) => el.remove());
    this.cloned = false;
    this.singleSetWidth = 0;
  }

  _onPointerDown(event) {
    if (!this.singleSetWidth) return;

    this.dragging = true;
    this.pointerId = event.pointerId;
    this.track.setPointerCapture(this.pointerId);
    this.startX = event.clientX;

    // Lees de huidige, door de CSS-animatie berekende positie af, zodat het
    // slepen exact daar overneemt zonder visuele sprong.
    const matrix = new DOMMatrixReadOnly(window.getComputedStyle(this.track).transform);
    this.startOffset = matrix.m41;

    this.track.classList.add('is-dragging');
    this.track.style.transform = `translateX(${this.startOffset}px)`;

    this.track.addEventListener('pointermove', this._onPointerMove);
    this.track.addEventListener('pointerup', this._onPointerUp);
    this.track.addEventListener('pointercancel', this._onPointerUp);
  }

  _onPointerMove(event) {
    if (!this.dragging) return;

    const delta = event.clientX - this.startX;
    let offset = (this.startOffset + delta) % this.singleSetWidth;
    if (offset > 0) offset -= this.singleSetWidth;

    this.currentOffset = offset;
    this.track.style.transform = `translateX(${offset}px)`;
  }

  _onPointerUp() {
    if (!this.dragging) return;
    this._endDrag();

    // Hervat de animatie exact waar het slepen stopte via een negatieve
    // animation-delay, in plaats van terug te springen naar het begin.
    const durationRaw = window.getComputedStyle(this).getPropertyValue('--plf-marquee-duration');
    const duration = parseFloat(durationRaw) || 60;
    const progress = Math.abs(this.currentOffset) / this.singleSetWidth;

    this.track.style.transform = '';
    this.track.style.animationDelay = `-${progress * duration}s`;
    this.track.classList.remove('is-dragging');
  }

  _endDrag() {
    this.dragging = false;
    this.track.removeEventListener('pointermove', this._onPointerMove);
    this.track.removeEventListener('pointerup', this._onPointerUp);
    this.track.removeEventListener('pointercancel', this._onPointerUp);
  }
}

if (!customElements.get('ploufe-lifestyle-carousel')) {
  customElements.define('ploufe-lifestyle-carousel', PloufeLifestyleCarousel);
}
